import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getCookieUnnaxAttempts,
  isUnnaxStatusSuccess,
  removeCookieUnnaxAttempts,
} from 'utils/unnax';
import { usePolling } from 'hooks/usePolling';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

export type CHANGE_BANK_PLATFORM = 'TINK' | 'UNNAX';

export type Banks = {
  templates: [
    {
      clientIdentificationNumber: string;
      clientPersonalId: string;
      bankName: string;
      registrationNumber: string;
      accountNumber: string;
      bic: string;
      homePageUrl: string;
      branchPageUrl: string;
      receiver: string;
      masterAccount: boolean;
      shortName: string;
    },
  ];
};

export type Bank_status =
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'SUCCESS_SAME_IBAN_SELECTED'
  | 'ACCOUNT_NOT_VALID'
  | 'ERROR'
  | undefined;

type BanksContextType = {
  banks?: Banks;
  fetchBanks: () => Promise<Banks | void>;
  fetchTinkBankStatus: () => Promise<Bank_status | void>;
  fetchUnnaxBankStatus: () => Promise<Bank_status | void>;
  updateUnnaxBank: (newBankAccount: string) => Promise<void>;
  unnaxBankStatus: Bank_status;
  setUnnaxBankStatus: (status: Bank_status) => void;
  tinkBankStatus: Bank_status;
  setTinkBankStatus: (status: Bank_status) => void;
  changeBankPlatform: CHANGE_BANK_PLATFORM;
  setChangeBankPlatform: (platform: CHANGE_BANK_PLATFORM) => void;
};

const BanksContext = createContext<BanksContextType>({} as BanksContextType);

const useBanks = (): BanksContextType => useContext(BanksContext);

const BanksProvider = ({ children }: Props): JSX.Element => {
  const [changeBankPlatform, setChangeBankPlatform] =
    useState<CHANGE_BANK_PLATFORM>(
      getCookieUnnaxAttempts() >= 3 ? 'TINK' : 'UNNAX',
    );
  const [banks, setBanks] = useState<Banks>();
  const [tinkBankStatus, setTinkBankStatus] = useState<
    Bank_status | undefined
  >();
  const [unnaxBankStatus, setUnnaxBankStatus] = useState<
    Bank_status | undefined
  >();
  const { showError } = useError();

  const fetchUnnaxBankStatus =
    useCallback(async (): Promise<Bank_status | void> => {
      try {
        const response = await axiosInstance.get(
          '/client/bank-account-verification-status',
        );

        const isSuccess = isUnnaxStatusSuccess(response.data.status);
        if (isSuccess) removeCookieUnnaxAttempts();

        setUnnaxBankStatus(response.data.status);

        return response.data.status;
      } catch (e) {
        showError();
        throw e;
      }
    }, [showError]);

  const bankPolling = usePolling(fetchUnnaxBankStatus);

  // stop polling and restart status if FE changes the bank platform
  useEffect(() => {
    setUnnaxBankStatus(undefined);
  }, [changeBankPlatform]);

  // Stop polling when the tink process finish
  useEffect(() => {
    if (
      bankPolling.pollingAttempts &&
      unnaxBankStatus &&
      unnaxBankStatus !== 'IN_PROGRESS'
    ) {
      bankPolling.stopPolling();
    }
  }, [unnaxBankStatus]);

  const fetchBanks = useCallback(async (): Promise<Banks | void> => {
    try {
      const fetchedBanks = await axiosInstance.get('/client/banks');

      setBanks(fetchedBanks.data);

      return fetchedBanks.data;
    } catch (e) {
      showError();
      throw e;
    }
  }, [showError]);

  const fetchTinkBankStatus =
    useCallback(async (): Promise<Bank_status | void> => {
      try {
        const response = await axiosInstance.get(
          '/client/tink/bank-update-status',
        );

        setTinkBankStatus(response.data.status);

        return response.data.status;
      } catch (e) {
        showError();
        throw e;
      }
    }, [showError]);

  // Update bank using Unnax platform
  const updateUnnaxBank = useCallback(
    async (newBankAccount: string): Promise<void> => {
      try {
        setUnnaxBankStatus('IN_PROGRESS');
        await axiosInstance.post('/client/bank-account-verification', {
          clientIbanAccount: newBankAccount,
        });

        bankPolling.startPolling(5000);
      } catch (e) {
        setUnnaxBankStatus('ERROR');
      }
    },
    [showError],
  );

  const banksContextValue = useMemo(
    () => ({
      banks,
      unnaxBankStatus,
      tinkBankStatus,
      fetchBanks,
      fetchTinkBankStatus,
      fetchUnnaxBankStatus,
      setUnnaxBankStatus,
      setTinkBankStatus,
      updateUnnaxBank,
      changeBankPlatform,
      setChangeBankPlatform,
    }),
    [
      banks,
      fetchBanks,
      fetchTinkBankStatus,
      fetchUnnaxBankStatus,
      unnaxBankStatus,
      tinkBankStatus,
      setUnnaxBankStatus,
      setTinkBankStatus,
      updateUnnaxBank,
      changeBankPlatform,
      setChangeBankPlatform,
    ],
  );

  return (
    <BanksContext.Provider value={banksContextValue}>
      {children}
    </BanksContext.Provider>
  );
};

export { useBanks as default, BanksProvider };
